import {
  findAuthor,
  getThreadStaticSuggestions,
  makeRepositoryReference,
  referenceArraysAreEqual,
  referenceID,
  referencesAreEqual,
  renderableReferenceTypes,
} from '@github-ui/copilot-chat-helpers/copilot-chat-helpers'
import {
  addMessageToHierarchy,
  constructMessagesHierarchy,
  getActiveMessages,
  selectActiveMessage,
  unselectPreviousChild,
  updateCompletedMessageParent,
} from '@github-ui/copilot-chat-helpers/copilot-chat-subthreading-helpers'
import {copilotLocalStorage} from '@github-ui/copilot-chat-helpers/copilot-local-storage'
import {customCopilotMatchesId} from '@github-ui/copilot-chat-helpers/custom-copilots-helpers'
import type {
  ContentPart,
  CopilotChatMessage,
  CopilotChatReference,
  CopilotChatThread,
  IndexCopilotSpace,
  SkillExecution,
} from '@github-ui/copilot-chat-types'
import isEqual from 'lodash-es/isEqual'

import {
  computeReplacedReferences,
  finalizeStreamingExecutions,
  findMatchingExecutionIndex,
} from './copilot-chat-reducer-helpers'
import type {
  AmbientError as ReducerAmbientError,
  CopilotChatAction as ReducerCopilotChatAction,
  CopilotChatState as ReducerCopilotChatState,
  CopilotChatThreadPatch as ReducerCopilotChatThreadPatch,
  Dispatcher as ReducerDispatcher,
  LoadingState as ReducerLoadingState,
  LoadingStateState as ReducerLoadingStateState,
  SharedResponse as ReducerSharedResponse,
  Timings as ReducerTimings,
  TraceData as ReducerTraceData,
} from './copilot-chat-reducer-types'

export type AmbientError = ReducerAmbientError
export type CopilotChatAction = ReducerCopilotChatAction
export type CopilotChatState = ReducerCopilotChatState
export type CopilotChatThreadPatch = ReducerCopilotChatThreadPatch
export type Dispatcher = ReducerDispatcher
export type LoadingState = ReducerLoadingState
export type LoadingStateState = ReducerLoadingStateState
export type SharedResponse = ReducerSharedResponse
export type Timings = ReducerTimings
export type TraceData = ReducerTraceData

// A reference is "ambient" for suggestion purposes when it is not shown as a chip
// (i.e. not in renderableReferenceTypes) or when it is a github-url reference
// which is treated as context-providing but not user-initiated.
function isAmbientSuggestionRef(ref: CopilotChatReference): boolean {
  return !renderableReferenceTypes().has(ref.type) || ref.type === 'github-url'
}

export const copilotChatReducer = (state: CopilotChatState, action: CopilotChatAction): CopilotChatState => {
  const setSelectedThreadID = (selectedThreadID: string | null): CopilotChatState => {
    // always sync localStorage with current thread ID (including null) to prevent stale data
    copilotLocalStorage.selectedThreadID = selectedThreadID

    const isNewSelectedThread = selectedThreadID !== state.selectedThreadID

    const modelID = copilotLocalStorage.getModel(selectedThreadID)?.id
    const model = state.availableModels?.find(m => m.id === modelID) ?? state.model

    return {
      ...state,
      model,
      autoTier: copilotLocalStorage.getAutoTier(selectedThreadID),
      selectedThreadID,
      threadSelectedAt: isNewSelectedThread ? new Date() : state.threadSelectedAt,
      currentView: 'thread',
      showTopicPicker: selectedThreadID ? false : state.showTopicPicker,
      currentReferences: isNewSelectedThread ? [] : state.currentReferences,
      threadHasNewMessages: isNewSelectedThread ? false : state.threadHasNewMessages,
      editingMessage: isNewSelectedThread ? undefined : state.editingMessage,
      pendingFirstMessage: isNewSelectedThread ? undefined : state.pendingFirstMessage,
    }
  }

  switch (action.type) {
    case 'SLASH_COMMANDS_ERROR': {
      return {
        ...state,
        slashCommandLoading: {...state.slashCommandLoading, state: 'error'},
      }
    }
    case 'SLASH_COMMANDS_LOADED': {
      return {
        ...state,
        slashCommandLoading: {...state.slashCommandLoading, state: 'loaded'},
      }
    }
    case 'SLASH_COMMANDS_LOADING': {
      return {
        ...state,
        slashCommandLoading: {...state.slashCommandLoading, state: 'loading'},
      }
    }
    case 'OPEN_COPILOT_CHAT':
      return {
        ...state,
        chatIsOpen: true,
        chatIsVisible: true,
        entryPointId: action.id,
        skipImplicitReferences: false,
      }
    case 'CLOSE_COPILOT_CHAT':
      return {
        ...state,
        chatIsOpen: false,
        skipImplicitReferences: false,
      }
    case 'HIDE_COPILOT_CHAT':
      return {
        ...state,
        chatIsVisible: false,
      }
    case 'THREAD_CREATED': {
      const newThreads = new Map(state.threads)
      newThreads.set(action.thread.id, action.thread)

      return {
        ...(action.preventThreadSelection ? state : setSelectedThreadID(action.thread.id)),
        threads: newThreads,
        // Preserve pendingFirstMessage so the user's message remains visible while
        // the thread is being set up. MESSAGE_ADDED will clear it once the real
        // message is added to the hierarchy.
        pendingFirstMessage: state.pendingFirstMessage,
      }
    }
    case 'THREAD_CONTINUED': {
      const newThreads = new Map(state.threads)
      newThreads.set(action.thread.id, action.thread)
      return {
        ...setSelectedThreadID(action.thread.id),
        threads: newThreads,
      }
    }
    case 'THREAD_CONTINUED_FAILED':
      return {
        ...state,
      }
    case 'SUGGESTIONS_GENERATED': {
      // Suggestions are driven by references — block URL-based generation when any refs are present
      if (state.currentReferences.length > 0) return state
      if (state.suggestionsContext) return state
      return {
        ...state,
        suggestions: action.suggestions,
      }
    }
    case 'CLEAR_SUGGESTIONS':
      if (state.suggestionsContext && state.currentReferences.length === 0) {
        return state
      }
      // Suggestions are ref-driven — don't clear them while refs are attached
      if (state.currentReferences.length > 0) {
        return state
      }
      return {
        ...state,
        suggestions: null,
      }
    case 'CLEAR_CURRENT_REFERENCES': {
      const keepTypes = action.keepTypes && new Set(action.keepTypes)

      const clearedRefs = keepTypes
        ? state.currentReferences.filter(reference => {
            return keepTypes.has(reference.type)
          })
        : []

      return {
        ...state,
        currentReferences: clearedRefs,
      }
    }
    case 'CLEAR_IMPLICIT_REFERENCES': {
      if (state.implicitReferences.size === 0) {
        return state
      }
      const filteredReferences = state.currentReferences.filter(ref => !state.implicitReferences.has(ref))
      const currentReferences =
        filteredReferences.length === state.currentReferences.length ? state.currentReferences : filteredReferences
      return {
        ...state,
        currentReferences,
        implicitReferences: new Set(),
      }
    }
    case 'PROMOTE_IMPLICIT_REFERENCE': {
      if (!state.implicitReferences.has(action.reference)) {
        return state
      }
      const newImplicitRefs = new Set(state.implicitReferences)
      newImplicitRefs.delete(action.reference)
      return {
        ...state,
        implicitReferences: newImplicitRefs,
      }
    }
    case 'CLEAR_ALL_REFERENCES': {
      return {
        ...state,
        currentReferences: [],
      }
    }
    case 'MESSAGES_UPDATED': {
      if (action.state === 'error') {
        // Invalidate auth token if messages not found or not authorized (possibly due to access issues)
        if ((action.notFound || action.notAuthorized) && action.missingOrgIds && action.missingOrgIds.length > 0) {
          copilotLocalStorage.clearAuthToken()
        }
      }
      // Only replace messages when the action explicitly provides them.
      // When action.messages is undefined (e.g. a loading-state-only update),
      // preserve the existing messages to avoid clearing client-side messages
      // that are being actively composed or streamed.
      if (action.messages) {
        const messages = [...action.messages]

        // Guard against a stale fetchMessages response (from a racing route sync)
        // overwriting client-side messages with an empty server response.
        // If the server returned nothing but we already have client-side messages,
        // a pending first message, or an active streaming session, keep what we have.
        // Only apply this guard when a thread is selected — when selectedThreadID is
        // null the user has navigated to "new chat" and we should allow clearing.
        const hasClientSideMessages =
          state.messages.length > 0 && state.messages.some(m => m.id !== 'root' && m.clientSide)
        const isSendingOrStreaming =
          state.pendingFirstMessage != null || state.isWaitingOnCopilot || state.streamingMessage != null
        if (
          messages.length === 0 &&
          state.selectedThreadID != null &&
          (hasClientSideMessages || isSendingOrStreaming)
        ) {
          return {
            ...state,
            messagesLoading: {
              ...state.messagesLoading,
              state: action.state,
              missingOrgIds: action.missingOrgIds,
              notFound: action.notFound,
              notAuthorized: action.notAuthorized,
            },
          }
        }

        let defaultRecipient = state.defaultRecipient
        let allClientConfirmations = state.allClientConfirmations
        if (action.state === 'loaded' && messages.length > 0) {
          const lastMsg = messages[messages.length - 1]
          if (lastMsg) defaultRecipient = getDefaultRecipient(lastMsg, state)
          allClientConfirmations = messages
            .map(message =>
              message.clientConfirmations?.map(cc => JSON.stringify(Object.values(cc.confirmation).sort())),
            )
            .flat()
            .filter(Boolean) as string[]
        }

        // Messages that came from server are not client-side by definition.
        // The server reconstructs interleaved contentParts on history messages
        // (copilot-chat#664), so we don't synthesise them here — interspersed
        // rendering consumes the server-provided ordering directly. Messages
        // without contentParts (e.g. pure text) fall back to the legacy layout.
        for (const message of messages) {
          const skillReferences = message.skillExecutions?.flatMap(execution => execution.references ?? []) ?? []
          if (skillReferences.length > 0) {
            const references = [...(message.references ?? [])]
            for (const reference of skillReferences) {
              const id = referenceID(reference)
              if (
                !references.some(
                  existingReference =>
                    existingReference === reference ||
                    (id !== '' ? referenceID(existingReference) === id : isEqual(existingReference, reference)),
                )
              ) {
                references.push(reference)
              }
            }
            message.references = references
          }
          message.clientSide = false
        }

        return {
          ...state,
          defaultRecipient,
          allClientConfirmations,
          messages: constructMessagesHierarchy(messages),
          messagesLoading: {
            ...state.messagesLoading,
            state: action.state,
            missingOrgIds: action.missingOrgIds,
            notFound: action.notFound,
            notAuthorized: action.notAuthorized,
          },
          messagesRestored: false,
        }
      } else {
        return {
          ...state,
          messagesLoading: {
            ...state.messagesLoading,
            state: action.state,
            missingOrgIds: action.missingOrgIds,
            notFound: action.notFound,
            notAuthorized: action.notAuthorized,
          },
        }
      }
    }
    case 'WAITING_ON_COPILOT':
      return {
        ...state,
        isWaitingOnCopilot: action.loading,
        threadHasNewMessages: true,
      }
    case 'WAITING_ON_ATTACHMENT':
      return {
        ...state,
        isWaitingOnAttachment: action.loading,
        attachmentProcessingType: action.loading ? action.attachmentType || null : null,
      }

    case 'SELECT_THREAD': {
      const result: CopilotChatState = {
        ...setSelectedThreadID(action.thread?.id || null),
        currentTopic: action.clearTopic ? undefined : state.currentTopic,
        topicLoading: action.clearTopic ? {error: null, state: 'loaded'} : state.topicLoading,
      }

      // Reset the references to the current repo if navigating to a new thread. Otherwise, keep the value that comes
      // from setSelectedThreadID, which resets the references on navigating across threads
      if (action.thread === null && state.currentRepository) {
        result.currentReferences = [makeRepositoryReference(state.currentRepository)]
      }

      return result
    }
    case 'HANDLE_EVENT_START': {
      const hasExplicitReferences = Boolean(action.references && action.references.length > 0)
      const latestReference = (() => {
        if (!action.references?.length) return undefined
        return (
          action.references.findLast(r => r.type !== 'github-url') ?? action.references[action.references.length - 1]
        )
      })()
      const suggestionSource =
        latestReference ??
        (action.suggestionsContext ? ({type: action.suggestionsContext} as CopilotChatReference) : null)
      return {
        ...state,
        chatIsOpen: true,
        chatIsVisible: true,
        entryPointId: action.id,
        currentReferences: action.references ?? state.currentReferences,
        suggestionsContext: hasExplicitReferences ? undefined : action.suggestionsContext,
        suggestions: suggestionSource ? getThreadStaticSuggestions(suggestionSource) : state.suggestions,
        // Skip the topic picker when suggestionsContext is provided OR when explicit references are attached
        // (e.g. opening from a non-repo page with context-specific suggestions, or clicking a file-diff button)
        showTopicPicker: action.suggestionsContext || hasExplicitReferences ? false : state.showTopicPicker,
        customInstructions: action.customInstructions ?? state.customInstructions,
        skipImplicitReferences: action.skipImplicitReferences ?? false,
      }
    }
    case 'UPDATE_SUGGESTIONS_CONTEXT': {
      if (!state.chatIsOpen) return state
      return {
        ...state,
        suggestionsContext: action.suggestionsContext,
        suggestions: getThreadStaticSuggestions({type: action.suggestionsContext} as CopilotChatReference),
        customInstructions: action.customInstructions ?? state.customInstructions,
      }
    }
    case 'THREADS_LOADING':
      return {
        ...state,
        threadsLoading: {...state.threadsLoading, state: 'loading'},
      }
    case 'LATEST_THREAD_LOADED': {
      const newThreads = new Map(state.threads)
      newThreads.set(action.latestThread.id, action.latestThread)
      return {
        ...state,
        threads: newThreads,
      }
    }
    case 'THREADS_LOADED': {
      // There is a possibility that until threads are loaded, a new thread is created
      // We want to make sure we don't overwrite that thread
      // If the selected thread has been created recently and in case doesn't exist in the loaded threads, we'll keep it
      const selectedThread = state.selectedThreadID && state.threads.get(state.selectedThreadID)
      const isSelectedThreadRecent =
        selectedThread && Date.now() - new Date(selectedThread.createdAt).getTime() < 1 * 60 * 1000 // 1 minute
      if (isSelectedThreadRecent && !action.threads.find(t => t.id === selectedThread.id)) {
        action.threads.push(selectedThread)
      }

      return {
        ...state,
        threadsLoading: {...state.threadsLoading, state: 'loaded'},
        threads: new Map(action.threads.map(t => [t.id, t])),
        orgsFailedToLoad: action.orgsFailedToLoad ?? state.orgsFailedToLoad,
      }
    }
    case 'THREADS_LOADING_ERROR':
      return {
        ...state,
        threadsLoading: {error: action.message, state: 'error', status: action.status},
      }
    case 'DELETE_THREAD_KEEP_SELECTION': {
      const newThreads = new Map(state.threads)
      newThreads.delete(action.thread.id)

      return {
        ...setSelectedThreadID(null),
        threads: newThreads,
        threadsLoading: {...state.threadsLoading, state: 'loaded'},
        messages: [],
        messagesLoading: {state: 'loaded', error: null},
        currentReferences: [],
        currentView: 'list',
      }
    }
    case 'DELETE_ALL_THREADS_KEEP_SELECTION': {
      const newThreads = new Map(state.threads)
      for (const thread of action.threads) {
        newThreads.delete(thread.id)
      }

      return {
        ...setSelectedThreadID(null),
        threads: newThreads,
        threadsLoading: {...state.threadsLoading, state: 'loaded'},
        messages: [],
        messagesLoading: {state: 'loaded', error: null},
        currentReferences: [],
        currentView: 'list',
      }
    }
    case 'DELETE_THREAD': {
      const newThreads = new Map(state.threads)
      newThreads.delete(action.thread.id)
      if (state.selectedThreadID === action.thread.id) {
        return {
          ...setSelectedThreadID(null),
          threads: newThreads,
          threadsLoading: {...state.threadsLoading, state: 'loaded'},
          messages: [],
          messagesLoading: {state: 'loaded', error: null},
          currentReferences: [],
        }
      } else {
        return {
          ...state,
          threads: newThreads,
        }
      }
    }

    case 'DELETE_THREAD_ERROR': {
      const newThreads = new Map(state.threads)
      newThreads.set(action.thread.id, action.thread)
      return {
        ...state,
        threadsLoading: {...state.threadsLoading, error: action.error},
        threads: newThreads,
      }
    }
    case 'DELETE_ALL_THREADS_ERROR': {
      const updatedThreads = new Map(state.threads)
      for (const thread of action.threads) {
        updatedThreads.set(thread.id, thread)
      }
      return {
        ...state,
        threadsLoading: {...state.threadsLoading, error: action.error},
        threads: updatedThreads,
      }
    }
    case 'MESSAGE_ADDED': {
      const messages = addMessageToHierarchy(state.messages, action.message)

      return {
        ...state,
        messages,
        // clear existing error so new messages can show
        messagesLoading: {state: 'loaded', error: null},
        pendingFirstMessage: undefined,
        threadHasNewMessages: true,
        editingMessage: undefined,
      }
    }
    case 'MESSAGE_FEEDBACK': {
      const messages = [...state.messages]
      const i = messages.findIndex(m => m.id === action.message.id)
      const message = messages[i]
      if (!message) {
        // this shouldn't happen
        return state
      }

      messages[i] = {
        ...message,
        feedback: action.feedback,
      }

      return {
        ...state,
        messages,
      }
    }
    case 'MESSAGES_SET_SELECTED_MESSAGE': {
      const messages = selectActiveMessage(state.messages, action.message)

      return {
        ...state,
        messages,
        threadHasNewMessages: false,
        threadSelectedAt: new Date(),
      }
    }
    case 'MESSAGES_UNSELECT_PREVIOUS_MESSAGE': {
      const messages = unselectPreviousChild(state.messages, action.message)

      return {
        ...state,
        messages,
        threadHasNewMessages: false,
      }
    }
    case 'THREAD_UPDATED':
      return {
        ...state,
        threads: updateThread(state, action.thread),
      }
    case 'REFERENCES_LOADED':
      return {
        ...state,
        currentReferences: state.currentReferences
          // remove potential duplicates
          .filter(oldRef => action.references.every(newRef => referenceID(newRef) !== referenceID(oldRef)))
          .concat(action.references),
      }
    case 'ADD_REFERENCE': {
      const newCurrentReferences = [
        ...state.currentReferences.filter(reference => !referencesAreEqual(reference, action.reference)),
        action.reference,
      ]
      let newImplicitReferences = state.implicitReferences
      if (newCurrentReferences.length > state.currentReferences.length) {
        if (action.reference.type === 'repository') {
          copilotLocalStorage.setLastUsedRepository(action.reference)
        }
      }
      if (action.implicit) {
        const implicitReferences = new Set(newImplicitReferences)
        implicitReferences.add(action.reference)
        newImplicitReferences = implicitReferences
      }

      let newSuggestions = state.suggestions
      const hasNonAmbientExplicitRefs = newCurrentReferences.some(
        ref => !newImplicitReferences.has(ref) && !isAmbientSuggestionRef(ref),
      )
      if ((!action.implicit || !hasNonAmbientExplicitRefs) && !(action.implicit && state.suggestionsContext)) {
        const latestRef =
          newCurrentReferences.findLast(ref => !newImplicitReferences.has(ref) && !isAmbientSuggestionRef(ref)) ??
          newCurrentReferences.findLast(ref => !isAmbientSuggestionRef(ref)) ??
          newCurrentReferences.findLast(ref => !newImplicitReferences.has(ref)) ??
          newCurrentReferences[newCurrentReferences.length - 1]
        newSuggestions = latestRef ? getThreadStaticSuggestions(latestRef) : state.suggestions
      }

      return {
        ...state,
        currentReferences: newCurrentReferences,
        implicitReferences: newImplicitReferences,
        suggestions: newSuggestions,
      }
    }
    case 'REMOVE_REFERENCES': {
      const removedReferences = action.references

      const LIST_PAGE_REF_TYPES = new Set(['issues', 'pull-requests', 'discussions'])
      const removingGithubUrl = removedReferences.some(r => r.type === 'github-url')
      const effectiveRemovedReferences = removingGithubUrl
        ? [...removedReferences, ...state.currentReferences.filter(r => LIST_PAGE_REF_TYPES.has(r.type))]
        : removedReferences

      // Update references in local storage, these could be different from currentReferences so update separately
      const storageReferences = copilotLocalStorage.getCurrentReferences(state.selectedThreadID)
      if (storageReferences) {
        const updatedStorageReferences = storageReferences.filter(
          r => !effectiveRemovedReferences.some(removedRef => referencesAreEqual(removedRef, r)),
        )
        copilotLocalStorage.setCurrentReferences(state.selectedThreadID, updatedStorageReferences)
      }

      const newCurrentReferences = state.currentReferences.filter(r => !effectiveRemovedReferences.includes(r))
      const newImplicitReferences = new Set(
        [...state.implicitReferences].filter(r => !effectiveRemovedReferences.includes(r)),
      )
      const lastExplicitItemRef =
        newCurrentReferences.findLast(ref => !newImplicitReferences.has(ref) && !isAmbientSuggestionRef(ref)) ?? null
      const lastAnyItemRef = newCurrentReferences.findLast(ref => !isAmbientSuggestionRef(ref)) ?? null
      const lastAmbientRef = newCurrentReferences[newCurrentReferences.length - 1] ?? null
      const suggestionSource = lastExplicitItemRef ?? lastAnyItemRef ?? lastAmbientRef ?? state.currentRepository
      const newSuggestions = suggestionSource
        ? getThreadStaticSuggestions(suggestionSource)
        : getThreadStaticSuggestions({type: 'default'} as unknown as CopilotChatReference)
      return {
        ...state,
        currentReferences: newCurrentReferences,
        implicitReferences: newImplicitReferences,
        suggestions: newSuggestions,
      }
    }
    case 'REPLACE_REFERENCE': {
      const {updatedReferences, implicitReferences} = computeReplacedReferences(
        state.currentReferences,
        state.implicitReferences,
        action.referenceToDelete,
        action.referenceToInsert,
      )

      const lastRef =
        updatedReferences.findLast(ref => !implicitReferences.has(ref) && !isAmbientSuggestionRef(ref)) ??
        updatedReferences.findLast(ref => !isAmbientSuggestionRef(ref)) ??
        updatedReferences[updatedReferences.length - 1]
      const newSuggestions = lastRef ? getThreadStaticSuggestions(lastRef) : state.suggestions

      return {
        ...state,
        implicitReferences,
        currentReferences: updatedReferences,
        suggestions: newSuggestions,
      }
    }
    case 'SHOW_TOPIC_PICKER':
      return {
        ...state,
        showTopicPicker: action.show,
      }
    case 'CURRENT_TOPIC_UPDATED':
      return {
        ...state,
        currentTopic: action.topic,
        topicLoading: {...state.topicLoading, state: action.state},
      }
    case 'MESSAGE_STREAMING_STARTED':
      return {
        ...state,
        isWaitingOnCopilot: true,
        streamingMessage: action.message,
        messages: [...state.messages],
      }
    case 'MESSAGE_STREAMING_TOKEN_ADDED':
      return {
        ...state,
        streamingMessage: state.streamingMessage
          ? {
              ...state.streamingMessage,
              content: state.streamingMessage.content + action.token,
              contentParts: appendTextToContentParts(state.streamingMessage.contentParts, action.token),
            }
          : null,
      }
    case 'MESSAGE_STREAMING_ROUTED_MODEL':
      return state.streamingMessage
        ? {
            ...state,
            streamingMessage: {...state.streamingMessage, model: action.model},
          }
        : state
    case 'MESSAGE_STREAMING_FUNCTION_CALLED':
      if (state.streamingMessage) {
        const prevExecutions = state.streamingMessage.skillExecutions || []

        const matchingIdx = findMatchingExecutionIndex(prevExecutions, action)
        const matchingExecution = matchingIdx >= 0 ? prevExecutions[matchingIdx] : null

        // Default to the previous executions if somehow we don't hit our expected conditions in the switch statement
        let executions = prevExecutions
        let references = state.streamingMessage.references || []

        switch (action.status) {
          // If status is started, append to skillExecutions
          case 'started':
            executions = [
              ...prevExecutions,
              {
                slug: action.name,
                status: action.status,
                arguments: action.arguments,
                errorMessage: action.errorMessage,
                references: [],
                callId: action.callId,
              },
            ]
            break
          case 'progress':
            // Mark the matching execution as progress, and update its status message.
            // Heuristic fallback is OK for status updates
            if (matchingExecution) {
              const updatedExecution = {
                ...matchingExecution,
                status: action.status,
                statusMessage: action.statusMessage,
              }
              // Create a new array with the updated execution at the correct index.
              executions = [
                ...prevExecutions.slice(0, matchingIdx),
                updatedExecution,
                ...prevExecutions.slice(matchingIdx + 1),
              ]
            }
            break
          case 'completed':
            // Mark the matching execution as completed, and update its references.
            // References require callId match - don't attach refs to wrong execution
            if (matchingExecution) {
              executions = [...prevExecutions]
              // Treat execution references as immutable once set. Only update if empty.
              let executionRefs = matchingExecution.references
              if (!matchingExecution.references?.length) {
                executionRefs = action.references
                references = Array.from(new Set([...references, ...action.references]))
              }
              executions[matchingIdx] = {
                ...matchingExecution,
                status: action.status,
                references: executionRefs,
              }
            }
            break
          case 'error':
            // Mark the matching execution as errored.
            if (matchingExecution) {
              executions = [...prevExecutions]
              executions[matchingIdx] = {
                ...matchingExecution,
                status: action.status,
                errorMessage: action.errorMessage,
              }
            }
            break
        }

        return {
          ...state,
          streamingMessage: {
            ...state.streamingMessage,
            skillExecutions: executions,
            contentParts: updateToolCallInContentParts(
              state.streamingMessage.contentParts,
              executions,
              action.status === 'started'
                ? {
                    slug: action.name,
                    status: action.status,
                    arguments: action.arguments,
                    errorMessage: action.errorMessage,
                    references: [],
                    callId: action.callId,
                  }
                : undefined,
              matchingIdx,
            ),
            references,
          },
        }
      }
      return state
    case 'MESSAGE_STREAMING_COMPLETED': {
      let defaultRecipient = state.defaultRecipient
      let newMessage: CopilotChatMessage | undefined
      let messages = state.messages

      if (state.streamingMessage) {
        // This is a workaround to set all function calls to 'completed' once streaming is done because we never
        // emit completed events from CAPI.
        // TODO - remove this workaround once we properly send 'completed' events for function calls
        // https://github.com/github/copilot-experiences/issues/8958
        const updatedExecutions = finalizeStreamingExecutions(state.streamingMessage.skillExecutions)
        newMessage = {
          ...state.streamingMessage,
          id: action.messageResponse.id,
          references: action.messageResponse.references,
          createdAt: action.messageResponse.createdAt,
          intent: action.messageResponse.intent,
          copilotAnnotations: action.messageResponse.copilotAnnotations,
          parentMessageID: action.messageResponse.parentMessageID,
          model: action.messageResponse.model,
          clientSide: false, // streamingMessage sets it to true, but we now got answer from server
          skillExecutions: updatedExecutions,
          contentParts: finalizeContentParts(state.streamingMessage.contentParts, updatedExecutions),
          usage: action.messageResponse.usage,
        }
        defaultRecipient = getDefaultRecipient(newMessage, state)

        // Record the requestID to localstorage now that we have the actual messageID
        if (state.streamingMessage.requestID) {
          copilotLocalStorage.addRequestID(newMessage.id, state.streamingMessage?.requestID)
        }

        messages = updateCompletedMessageParent(messages, newMessage)

        messages = addMessageToHierarchy(messages, newMessage)
      }

      return {
        ...state,
        defaultRecipient,
        isWaitingOnCopilot: false,
        streamingMessage: null,
        messages,
      }
    }
    case 'MESSAGE_STREAMING_FAILED':
      return {
        ...state,
        isWaitingOnCopilot: false,
        streamingMessage: null,
      }
    case 'MESSAGE_STREAMING_STOPPED': {
      let newMessage: CopilotChatMessage | undefined
      // Attach incomplete messages to the last thread
      // Note that to continue this subthread from that point, we will reload thread from server to get correct server state
      const parentMessageID = getActiveMessages(state.messages).findLast(m => m.role === 'user')?.id

      if (state.streamingMessage) {
        // response already started streaming, just mark it as interrupted and insert it into the list
        newMessage = {
          ...state.streamingMessage,
          interrupted: true,
          parentMessageID,
        }
      } else if (state.isWaitingOnCopilot) {
        // response hasn't started streaming yet, so just insert a dummy interrupted message
        newMessage = {
          id: crypto.randomUUID(),
          role: 'assistant',
          threadID: state.selectedThreadID ?? '',
          references: [],
          createdAt: new Date().toISOString(),
          interrupted: true,
          clientSide: true,
          parentMessageID,
        }
      }

      let messages = state.messages
      if (newMessage != null) {
        // While streaming, the user message isn't yet saved, even though it's part of `state.messages`. This means
        // that the streaming copilot message is linked to the last AI message, not its ephemeral user message.
        // Set the newMessage's parentMessageID to the user message, rather than the previous copilot response.
        const lastUserMessage = messages.findLast(m => m.role === 'user')
        if (lastUserMessage !== undefined) {
          newMessage.parentMessageID = lastUserMessage.id
        }

        messages = addMessageToHierarchy(messages, newMessage)
      }
      return {
        ...state,
        isWaitingOnCopilot: false,
        streamingMessage: null,
        messages,
      }
    }
    case 'MESSAGES_CLEAR_LAST_ERROR': {
      const activeMessages = getActiveMessages(state.messages)
      const allMessages = state.messages
      const lastMessage = activeMessages[activeMessages.length - 1]
      const lastMessageHasError = lastMessage?.error || lastMessage?.interrupted
      let filteredMessages = allMessages
      if (lastMessageHasError) {
        filteredMessages = allMessages.filter(message => message.id !== lastMessage?.id)
      }
      return {
        ...state,
        streamingMessage: null,
        messages: [...filteredMessages],
      }
    }
    case 'SELECT_REFERENCE':
      return {
        ...state,
        selectedReference: action.reference,
      }
    case 'MODELS_LOADING':
      return {
        ...state,
        modelsLoading: {
          state: 'loading',
          error: null,
        },
      }
    case 'MODELS_LOADING_ERROR':
      return {
        ...state,
        modelsLoading: {
          state: 'error',
          error: action.error,
        },
      }
    case 'MODELS_LOADED': {
      const models = action.models

      // Re-resolve the thread's stored model now that models are loaded.
      // This handles the race condition where SELECT_THREAD ran before models were loaded,
      // causing the thread's stored model to be lost in favor of the initial default.
      const resolvedModel = (() => {
        if (state.selectedThreadID) {
          const threadStoredModel = copilotLocalStorage.getModel(state.selectedThreadID)
          if (threadStoredModel) {
            const resolved = (models || []).find(m => m.id === threadStoredModel.id)
            if (resolved) return resolved
          }
        }
        return (models || []).find(m => m.id === state.model.id) || state.model
      })()

      return {
        ...state,
        model: resolvedModel,
        availableModels: models || [],
        modelsLoading: {
          state: 'loaded',
          error: null,
        },
        ...('autoModeDiscounts' in action && {autoModeDiscounts: action.autoModeDiscounts}),
      }
    }
    case 'SELECT_MODEL': {
      const selectedModel = action.model
      // We can have image references set that aren't supported by newly selected models.
      // This will drop them.
      const supportedReferences = selectedModel?.capabilities?.supports?.vision
        ? state.currentReferences
        : state.currentReferences.filter(reference => reference.type !== 'image')

      return {
        ...state,
        model: selectedModel,
        currentReferences: supportedReferences,
      }
    }
    case 'SELECT_AUTO_TIER':
      return {
        ...state,
        autoTier: action.autoTier,
      }
    case 'VIEW_ALL_THREADS':
      return {
        ...state,
        currentView: 'list',
      }
    case 'VIEW_CURRENT_THREAD':
      return {
        ...state,
        currentView: 'thread',
      }
    case 'IMPLICIT_CONTEXT_UPDATED':
      if (!referenceArraysAreEqual(action.context, state.context)) {
        return {
          ...state,
          context: action.context,
        }
      }
      return state
    case 'SET_TOP_REPOSITORIES':
      return {
        ...state,
        topRepositoriesCache: action.topics,
      }

    case 'SET_CUSTOM_COPILOT': {
      const existing = state.customCopilots?.find(copilot => customCopilotMatchesId(copilot, action.customCopilot))
      const partial = existing && action.partial
      const customCopilots = existing
        ? state.customCopilots?.map(copilot => {
            if (!customCopilotMatchesId(copilot, existing)) return copilot
            if (partial) return {...copilot, ...action.customCopilot}
            return action.customCopilot as IndexCopilotSpace
          })
        : [...(state.customCopilots ?? []), action.customCopilot as IndexCopilotSpace]

      return {
        ...state,
        customCopilots,
      }
    }
    case 'SET_CUSTOM_COPILOTS':
      return {
        ...state,
        customCopilots: action.customCopilots,
      }
    case 'REMOVE_CUSTOM_COPILOT': {
      return {
        ...state,
        customCopilots: state.customCopilots?.filter(
          copilot => !customCopilotMatchesId(copilot, action.customCopilotId),
        ),
      }
    }
    case 'MESSAGE_STREAMING_CONFIRMATION': {
      const confirmation = {
        title: action.title,
        message: action.message,
        confirmation: action.confirmation,
      }
      return {
        ...state,
        streamingMessage: state.streamingMessage
          ? {
              ...state.streamingMessage,
              confirmations: state.streamingMessage.confirmations
                ? [...state.streamingMessage.confirmations, confirmation]
                : [confirmation],
            }
          : null,
      }
    }
    case 'AGENT_ERROR': {
      const streamingMessage = state.streamingMessage
      const agentError = action.error

      return {
        ...state,
        streamingMessage: streamingMessage && {
          ...streamingMessage,
          agentErrors: [
            ...(streamingMessage.agentErrors ?? []),
            {
              type: agentError.type,
              code: agentError.code,
              message: agentError.message,
              identifier: agentError.identifier,
            },
          ],
        },
      }
    }
    case 'ENTITLEMENT_UPDATED':
      return {
        ...state,
        entitlement: action.entitlement ?? state.entitlement,
      }
    case 'TOGGLE_REPO_CUSTOM_INSTRUCTIONS':
      return {
        ...state,
        repoCustomInstructionsEnabled: action.value,
      }
    case 'DISMISS_EDITOR_UPSELL_BANNER':
      return {
        ...state,
        isEditorUpsellBannerDismissed: true,
      }
    case 'DISMISS_COPILOT_SPACES_MCP_BANNER':
      return {
        ...state,
        showCopilotSpacesMcpBanner: false,
      }
    case 'DISMISS_AMBIENT_ERROR':
      return {
        ...state,
        ambientError: null,
      }
    case 'ADD_AMBIENT_ERROR': {
      return {
        ...state,
        ambientError: {message: action.message},
      }
    }
    case 'SET_PERSONAL_INSTRUCTIONS':
      return {
        ...state,
        personalInstructions: action.personalInstructions,
      }
    case 'SHARED_THREAD_UPDATED': {
      const newThreads = updateThread(state, action.thread)
      return {
        ...state,
        threads: newThreads,
      }
    }
    case 'SHARED_THREADS_LOADING':
      return {
        ...state,
        sharedThreadsLoading: {...state.sharedThreadsLoading, state: 'loading'},
      }
    case 'SHARED_THREADS_LOADED':
      return {
        ...state,
        sharedThreadsLoading: {...state.sharedThreadsLoading, state: 'loaded'},
      }
    case 'SHARED_THREADS_LOADING_ERROR':
      return {
        ...state,
        sharedThreadsLoading: {...state.sharedThreadsLoading, state: 'error', error: action.message},
      }
    case 'IMAGE_UPLOADED': {
      return {
        ...state,
        currentReferences: state.currentReferences.map(reference => {
          if (reference.type === 'image' && reference.id === action.referenceId) {
            return {
              ...reference,
              imageUrl: action.dotcomAttachment.previewUrl,
            }
          }
          return reference
        }),
      }
    }
    case 'START_EDITING_MESSAGE':
      return {
        ...state,
        editingMessage: action.messageId,
      }
    case 'CANCEL_EDITING_MESSAGE':
      return {
        ...state,
        editingMessage: undefined,
      }
    case 'FETCH_SHARED_THREAD_MESSAGES':
      return {
        ...state,
        fetchSharedThreads: {
          ok: action.ok,
          status: action.status,
          shareId: action.shareId,
          originalThreadId: action.originalThreadId,
        },
      }
    case 'SET_WRAP_CODE_LINES':
      return {
        ...state,
        wrapCodeLines: action.value,
      }
    case 'CLEAR_SHARED_THREAD_CHANNEL':
      return {
        ...state,
        sharedThreadChannel: null,
      }
    case 'CLEAR_DEFAULT_RECIPIENT':
      return {
        ...state,
        defaultRecipient: undefined,
      }
    case 'SET_PENDING_FIRST_MESSAGE':
      return {
        ...state,
        pendingFirstMessage: action.message,
      }
    case 'SET_SHOULD_REPLACE_HISTORY_ON_NAVIGATION':
      return {
        ...state,
        shouldReplaceHistoryOnNavigation: action.value,
      }
  }
}

/**
 * Updates a thread using the given "patch" which contains a thread ID and some
 * subset of additional thread properties. Returns a copy of state.threads with
 * the thread in question updated, appropriate for returning in a new state.
 */
function updateThread(state: CopilotChatState, patch: CopilotChatThreadPatch): Map<string, CopilotChatThread> {
  const newThreads = new Map(state.threads)
  const storedThread = state.threads.get(patch.id)
  if (!storedThread) return newThreads

  const updatedThread: CopilotChatThread = {...storedThread, ...patch}
  newThreads.set(patch.id, updatedThread)

  return newThreads
}

function getDefaultRecipient(message: CopilotChatMessage, state: CopilotChatState): string | undefined {
  const author = findAuthor(message, state.currentUserLogin)
  if (author.type === 'agent') {
    return author.name
  }
}

// --- Content Parts helpers for interspersed tool call rendering ---

function appendTextToContentParts(parts: ContentPart[] | undefined, token: string): ContentPart[] {
  const currentParts = parts ?? []
  const lastPart = currentParts[currentParts.length - 1]
  if (lastPart && lastPart.type === 'text') {
    return [...currentParts.slice(0, -1), {type: 'text', content: lastPart.content + token}]
  }
  return [...currentParts, {type: 'text', content: token}]
}

/**
 * Update contentParts when a tool call event is received.
 * - For 'started': append a new toolCall content part.
 * - For progress/completed/error: update the toolCall part at the same positional
 *   index that `matchingIdx` identifies in `updatedExecutions`. Content parts and
 *   `updatedExecutions` are appended 1:1, so positional matching always agrees
 *   with whichever strategy the reducer used upstream (callId or heuristic).
 */
function updateToolCallInContentParts(
  parts: ContentPart[] | undefined,
  updatedExecutions: SkillExecution[],
  newExecution: SkillExecution | undefined,
  matchingIdx: number,
): ContentPart[] {
  const currentParts = parts ?? []

  if (newExecution) {
    return [...currentParts, {type: 'toolCall', skillExecution: newExecution}]
  }

  const matchedExecution = matchingIdx >= 0 ? updatedExecutions[matchingIdx] : undefined
  if (!matchedExecution) return currentParts

  let toolCallIdx = -1
  return currentParts.map(part => {
    if (part.type !== 'toolCall') return part
    toolCallIdx++
    if (toolCallIdx === matchingIdx) {
      return {type: 'toolCall', skillExecution: matchedExecution}
    }
    return part
  })
}

/**
 * Finalize contentParts when streaming completes. Ensures all tool call parts reflect
 * the final status from `updatedExecutions`. Parts with a callId match by callId first;
 * remaining parts fall back to positional matching, skipping execution indices already
 * claimed by a callId match so the two strategies never collide.
 */
function finalizeContentParts(
  parts: ContentPart[] | undefined,
  updatedExecutions: SkillExecution[] | undefined,
): ContentPart[] | undefined {
  if (!parts || !updatedExecutions) return parts

  const consumed = new Set<number>()
  for (const part of parts) {
    if (part.type !== 'toolCall') continue
    const callId = part.skillExecution.callId
    if (!callId) continue
    const idx = updatedExecutions.findIndex(exec => exec.callId === callId)
    if (idx >= 0) consumed.add(idx)
  }

  let nextPositional = 0
  return parts.map(part => {
    if (part.type !== 'toolCall') return part
    const callId = part.skillExecution.callId
    let finalExecution: SkillExecution | undefined
    if (callId) {
      finalExecution = updatedExecutions.find(exec => exec.callId === callId)
    } else {
      while (consumed.has(nextPositional)) nextPositional++
      finalExecution = updatedExecutions[nextPositional]
      nextPositional++
    }
    if (finalExecution) {
      return {type: 'toolCall', skillExecution: finalExecution}
    }
    return part
  })
}
